import { useWeb3 } from "logic/components";
import { TxAction, TxStep, useContractStaticCall, useHandleTx, useTxSteps } from "logic/hooks";
import { ERC20V2 } from "logic/pages/LoansPage/data/types";
import { useEffect } from "react";
import { erc20Abi, isAddress, zeroAddress } from "viem";

interface useWithdrawLocalTxParams {
  token: ERC20V2;
  chainId: number;
  amount: bigint;
  recipient: string;
  mainActionLabel?: string;
  enabled?: boolean;
}

export function useWithdrawLocalTx(params: useWithdrawLocalTxParams) {
  const { token, chainId, amount, recipient: _recipient, mainActionLabel = "Withdraw", enabled = true } = params;
  const { address: connectedAddress = zeroAddress } = useWeb3();
  const { steps, updateStepAtIndex, setSteps, currentStepIndex, isDone, currentStep, resetSteps } = useTxSteps(
    getInitialSteps(mainActionLabel),
  );
  const handleTx = useHandleTx();

  const recipient = isAddress(_recipient) ? _recipient : zeroAddress;

  const txsEnabled = Boolean(enabled && amount > 0 && connectedAddress !== zeroAddress && recipient !== zeroAddress);

  const { data: transferData, error: transferError } = useContractStaticCall({
    address: token.address,
    abi: erc20Abi,
    functionName: "transfer",
    args: [recipient, amount],
    chainId,
    query: {
      enabled: txsEnabled && !isDone,
    },
  });

  async function transfer() {
    return await handleTx({
      title: mainActionLabel,
      params: transferData?.request,
      updateStep: (updates) => updateStepAtIndex(0, updates),
    });
  }

  const withdrawActions: TxAction[] = [
    {
      send: transferData?.request && transfer,
      error: transferError,
    },
  ];

  const action = withdrawActions[currentStepIndex];

  useEffect(() => {
    setSteps(steps.map((s, i) => ({ ...s, error: withdrawActions[i]?.error })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferError]);

  return {
    steps,
    updateStepAtIndex,
    currentStepIndex,
    currentStep,
    resetSteps,

    action,
    isDone,
  };
}

function getInitialSteps(mainActionLabel: string): TxStep[] {
  return [
    {
      title: mainActionLabel,
      action: mainActionLabel,
      status: "idle",
    },
  ];
}
